import { AppInsightsErrorBoundary, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, ICustomProperties, SeverityLevel } from '@microsoft/applicationinsights-web';

let appInsightsInstance: ApplicationInsights | null = null;
const reactPlugin = new ReactPlugin();

const EXCEPTION_DEDUP_MS = 2000;
const exceptionTimestamps = new Map<string, number>();
const consoleErrorTimestamps = new Map<string, number>();
const cspTimestamps = new Map<string, number>();

const getCommonMetadata = (currentUrl: string): ICustomProperties => ({
	pageUrl: currentUrl,
	userAgent: navigator.userAgent,
	language: navigator.language,
	timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
	domain: window.location.hostname,
});

const shouldLog = (map: Map<string, number>, key: string, dedupMs: number): boolean => {
	const now = Date.now();
	const last = map.get(key) ?? 0;
	if (now - last <= dedupMs) return false;
	map.set(key, now);
	return true;
};

const getAppInsightsInstance = (): ApplicationInsights | null => appInsightsInstance;
const setAppInsightsInstance = (instance: ApplicationInsights | null): void => {
	appInsightsInstance = instance;
};

// Global JS error listener
window.onerror = function (message, source, lineno, colno, error) {
	try {
		const key = typeof message === 'string' ? message : String(message);
		if (!shouldLog(exceptionTimestamps, key, EXCEPTION_DEDUP_MS)) return;

		getAppInsightsInstance()?.trackException({
			exception: error instanceof Error ? error : new Error(String(message)),
			severityLevel: SeverityLevel.Error,
			properties: {
				...getCommonMetadata(window.location.href),
				source,
				lineno,
				colno,
				message: String(message),
			},
		});
	} catch (e) {
		console.warn('AppInsights onerror handler failed:', e);
	}
};

// Global unhandled Promise rejection listener
window.onunhandledrejection = function (event) {
	try {
		const key = typeof event.reason === 'string' ? event.reason : JSON.stringify(event.reason);
		if (!shouldLog(exceptionTimestamps, key, EXCEPTION_DEDUP_MS)) return;

		const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
		getAppInsightsInstance()?.trackException({
			exception: reason,
			severityLevel: SeverityLevel.Error,
			properties: {
				...getCommonMetadata(window.location.href),
			},
		});
	} catch (e) {
		console.warn('AppInsights unhandledrejection handler failed:', e);
	}
};

// Global CSP violation listener
document.addEventListener('securitypolicyviolation', (e: SecurityPolicyViolationEvent) => {
	try {
		const key = `${e.violatedDirective}|${e.blockedURI}`;
		if (!shouldLog(cspTimestamps, key, EXCEPTION_DEDUP_MS)) return;

		getAppInsightsInstance()?.trackTrace({
			message: `CSP violation: Blocked ${e.violatedDirective} on ${e.blockedURI}`,
			severityLevel: SeverityLevel.Warning,
			properties: {
				...getCommonMetadata(window.location.href),
				directive: e.violatedDirective,
				blockedURI: e.blockedURI,
				effectiveDirective: e.effectiveDirective,
				originalPolicy: e.originalPolicy,
				referrer: e.referrer,
				sourceFile: e.sourceFile,
				disposition: e.disposition,
				statusCode: e.statusCode,
			},
		});
	} catch (e) {
		console.warn('AppInsights CSP handler failed:', e);
	}
});

export const initAppInsightsLogger = (): void => {
	if (window.location.hostname === 'localhost') {
		console.warn('Skipping Application Insights initialization in localhost environment.');
		return;
	}

	try {
		const connectionString = document.querySelector('#app')?.getAttribute('data-appinsights-key') ?? '';
		const currentUrl = window.location.href;

		if (!connectionString) {
			console.warn('Warning: Application Insights connection string is missing or invalid.');
			return;
		}

		// The instrumentation key is being deprecated as of 31.03.2025.
		// Make sure to replace this with the new connection string in the config below.
		// https://learn.microsoft.com/en-us/azure/azure-monitor/app/javascript-framework-extensions?tabs=react
		const appInsights = new ApplicationInsights({
			config: {
				instrumentationKey: connectionString,
				enableAutoRouteTracking: true,
				extensions: [reactPlugin],
				extensionConfig: {
					[reactPlugin.identifier]: { history: '' },
				},
				disableExceptionTracking: false,
				disableTelemetry: false,
				maxBatchInterval: 0,
			},
		});

		appInsights.loadAppInsights();
		setAppInsightsInstance(appInsights);

		const originalConsoleError = console.error;

		console.error = (...args: any[]) => {
			try {
				const errorMessage = args.map((arg) => (typeof arg === 'string' ? arg : JSON.stringify(arg))).join(' ');

				if (!shouldLog(consoleErrorTimestamps, errorMessage, EXCEPTION_DEDUP_MS)) return;

				const metadata = getCommonMetadata(currentUrl);

				getAppInsightsInstance()?.trackTrace({
					message: `Console error: ${errorMessage}`,
					severityLevel: SeverityLevel.Error,
					properties: metadata,
				});
			} catch (e) {
				console.warn('AppInsights console.error wrapper failed:', e);
			} finally {
				originalConsoleError(...args);
			}
		};
	} catch (error) {
		console.error('Error initializing App Insights:', error);
		setAppInsightsInstance(null);
	}
};

export const isAppInsightsInitialized = (): boolean => {
	return !!getAppInsightsInstance() && typeof getAppInsightsInstance()!.trackTrace === 'function';
};

export const verbose = (message: string, properties: ICustomProperties | string): void => {
	if (isAppInsightsInitialized()) trace(message, SeverityLevel.Verbose, getProperties(properties));
};

export const information = (message: string, properties?: ICustomProperties | string): void => {
	if (isAppInsightsInitialized()) trace(message, SeverityLevel.Information, getProperties(properties));
};

export const warning = (message: string, properties?: ICustomProperties | string): void => {
	if (isAppInsightsInitialized()) trace(message, SeverityLevel.Warning, getProperties(properties));
};

export const critical = (exception: Error, message?: string, properties?: ICustomProperties | string): void => {
	if (isAppInsightsInitialized()) errorOrCritical(SeverityLevel.Critical, exception, message, properties);
};

export const exception = (exception: Error, message?: string, properties?: ICustomProperties | string): void => {
	if (isAppInsightsInitialized()) errorOrCritical(SeverityLevel.Error, exception, message, properties);
};

const errorOrCritical = (
	severityLevel: SeverityLevel,
	exception: Error,
	message?: string,
	properties?: ICustomProperties | string
): void => {
	if (!isAppInsightsInitialized()) return;
	const finalProperties = properties ? getProperties(properties) : { exception };
	const finalMessage = message ?? exception.name;
	getAppInsightsInstance()!.trackException({ exception, severityLevel }, finalProperties);
	trace(finalMessage, severityLevel, { ...finalProperties, exception });
};

const trace = (message: string, severityLevel: SeverityLevel, properties?: ICustomProperties): void => {
	if (!isAppInsightsInitialized()) return;
	getAppInsightsInstance()!.trackTrace({ message, severityLevel }, properties);
};

const getProperties = (properties: ICustomProperties | string | undefined): ICustomProperties | undefined => {
	if (!properties) return undefined;
	return typeof properties === 'string' ? { msg: properties } : properties;
};

export { AppInsightsErrorBoundary, reactPlugin };
